import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Package, MapPin, ArrowRight, LogOut, User, Edit3, Camera, Save, X } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { formatINR } from '../lib/utils-cynos';
import { formatISTDate } from '../utils/dateTime';
import ProfileInfo from '../components/customer/ProfileInfo';
import ProfileInput from '../components/customer/ProfileInput';

const emptyAddress = {
  full_name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
};

const buildForm = (customer = {}) => ({
  name: customer?.name || customer?.full_name || '',
  phone: customer?.phone || '',
  picture: customer?.picture || '',
  shipping_address: {
    ...emptyAddress,
    ...(customer?.shipping_address || {}),
    full_name: customer?.shipping_address?.full_name || customer?.name || customer?.full_name || '',
    phone: customer?.shipping_address?.phone || customer?.phone || '',
  },
});

export default function Profile() {
  const { customer, isAuthenticated, logout, refreshCustomer, updateCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(buildForm(customer));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Cynos Profile';
  }, []);

  useEffect(() => {
    if (isAuthenticated) refreshCustomer().catch(() => {});
  }, [isAuthenticated, refreshCustomer]);

  useEffect(() => {
    if (customer) setForm(buildForm(customer));
  }, [customer]);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updateAddress = (key, value) => setForm((current) => ({
    ...current,
    shipping_address: { ...current.shipping_address, [key]: value },
  }));

  const handleImage = (file) => {
    setError('');
    if (!file) return;
    if (!file.type.match(/^image\/(png|jpe?g|webp)$/)) {
      setError('Upload a PNG, JPG, or WebP profile image.');
      return;
    }
    if (file.size > 900 * 1024) {
      setError('Profile image must be smaller than 900KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateForm('picture', reader.result);
    reader.readAsDataURL(file);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const shipping = {
        ...form.shipping_address,
        full_name: form.shipping_address.full_name || form.name,
        phone: form.shipping_address.phone || form.phone,
      };
      await updateCustomer({
        name: form.name,
        phone: form.phone,
        picture: form.picture,
        shipping_address: shipping,
        billing_address: shipping,
      });
      setEditing(false);
      setMessage('Profile updated.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-md rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-2xl shadow-black/20 text-center">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-cynos-red/10 text-cynos-red mx-auto">
            <User className="h-8 w-8" />
          </div>
          <h1 className="text-4xl font-anton uppercase text-white">Your Profile</h1>
          <p className="mt-4 text-sm text-zinc-400">Sign in to see your saved orders, addresses, and support options.</p>
          <div className="mt-10 space-y-4">
            <button onClick={() => navigate('/signin')} className="w-full rounded-2xl bg-cynos-red px-5 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90">Sign In</button>
            <button onClick={() => navigate('/signup')} className="w-full rounded-2xl border border-white/10 bg-black/60 px-5 py-3 text-sm uppercase tracking-[0.2em] text-white transition hover:border-cynos-red hover:text-cynos-red">Create Account</button>
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black pt-24 pb-16 px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-6xl space-y-10">
        <section className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Account</p>
              <h1 className="mt-4 text-5xl font-anton uppercase text-white">Welcome back, {customer?.name || 'Cynos Member'}</h1>
              <p className="mt-3 max-w-2xl text-sm text-zinc-400">Manage your profile, track orders, and access support from one place.</p>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/60 px-5 py-3 text-sm uppercase tracking-[0.2em] text-white transition hover:border-cynos-red hover:text-cynos-red"
            >
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </div>
        </section>

        <section className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-8 shadow-xl shadow-black/20">
            <div className="space-y-6">
              <div className="rounded-3xl border border-white/10 bg-black/70 p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Customer Info</p>
                  {!editing ? (
                    <button onClick={() => { setEditing(true); setMessage(''); setError(''); }} className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/60 px-4 py-2 text-xs uppercase tracking-[0.2em] text-white hover:border-cynos-red hover:text-cynos-red">
                      <Edit3 className="h-4 w-4" /> Edit Profile
                    </button>
                  ) : (
                    <button onClick={() => { setEditing(false); setForm(buildForm(customer)); setError(''); }} className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-black/60 px-4 py-2 text-xs uppercase tracking-[0.2em] text-white hover:border-white">
                      <X className="h-4 w-4" /> Cancel
                    </button>
                  )}
                </div>

                {message && <div className="mt-4 rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">{message}</div>}
                {error && <div className="mt-4 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}

                {!editing ? (
                  <div className="mt-6 grid gap-6 lg:grid-cols-[120px_1fr]">
                    <div className="h-28 w-28 overflow-hidden rounded-full border border-white/10 bg-cynos-card">
                      {customer?.picture ? (
                        <img src={customer.picture} alt={customer?.name || 'Profile'} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-cynos-red"><User className="h-10 w-10" /></div>
                      )}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <ProfileInfo label="Name" value={customer?.name} />
                      <ProfileInfo label="Email" value={customer?.email} />
                      <ProfileInfo label="Phone" value={customer?.phone || 'Not provided'} />
                      <ProfileInfo label="Member since" value={formatISTDate(customer?.created_at || Date.now())} />
                    </div>
                  </div>
                ) : (
                  <form onSubmit={saveProfile} className="mt-6 space-y-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                      <div className="h-28 w-28 overflow-hidden rounded-full border border-white/10 bg-cynos-card">
                        {form.picture ? (
                          <img src={form.picture} alt="Profile preview" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-cynos-red"><User className="h-10 w-10" /></div>
                        )}
                      </div>
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-xs uppercase tracking-[0.2em] text-white hover:border-cynos-red hover:text-cynos-red">
                        <Camera className="h-4 w-4" /> Upload Image
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} />
                      </label>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <ProfileInput label="Full Name" value={form.name} onChange={(value) => updateForm('name', value)} required />
                      <ProfileInput label="Phone" value={form.phone} onChange={(value) => updateForm('phone', value)} required />
                      <ProfileInput label="Email" value={customer?.email || ''} disabled />
                      <ProfileInput label="Country" value={form.shipping_address.country} onChange={(value) => updateAddress('country', value)} required />
                    </div>

                    <div className="border-t border-white/10 pt-5">
                      <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Shipping Address</p>
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <ProfileInput label="Address Line 1" value={form.shipping_address.address_line1} onChange={(value) => updateAddress('address_line1', value)} />
                        <ProfileInput label="Address Line 2" value={form.shipping_address.address_line2} onChange={(value) => updateAddress('address_line2', value)} />
                        <ProfileInput label="City" value={form.shipping_address.city} onChange={(value) => updateAddress('city', value)} />
                        <ProfileInput label="State" value={form.shipping_address.state} onChange={(value) => updateAddress('state', value)} />
                        <ProfileInput label="Pincode" value={form.shipping_address.pincode} onChange={(value) => updateAddress('pincode', value)} />
                      </div>
                    </div>

                    <button disabled={saving} className="inline-flex items-center gap-2 rounded-2xl bg-cynos-red px-5 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:opacity-60">
                      <Save className="h-4 w-4" />
                      {saving ? 'Saving' : 'Save Profile'}
                    </button>
                  </form>
                )}
              </div>
              <div className="rounded-3xl border border-white/10 bg-cynos-card p-6">
                <div className="flex items-center gap-3 text-white">
                  <Package className="h-5 w-5 text-cynos-red" />
                  <h2 className="text-lg font-semibold">Order History</h2>
                </div>
                {customer?.order_history?.length ? (
                  <div className="mt-4 space-y-3">
                    {customer.order_history.slice().reverse().slice(0, 5).map((order) => (
                      <Link key={order.order_id} to={`/order/${order.order_id}`} className="block rounded-2xl border border-white/10 bg-black/50 p-4 hover:border-cynos-red">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">{order.order_id.slice(0, 8)}</span>
                          <span className="font-anton text-lg">{formatINR(order.total)}</span>
                        </div>
                        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">{(order.status || '').replaceAll('_', ' ')}</p>
                        {!!order.items?.length && (
                          <div className="mt-3 space-y-1">
                            {order.items.slice(0, 2).map((item, index) => (
                              <p key={`${order.order_id}-${index}`} className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">
                                {item.name} - Size {item.size}{item.color ? ` - ${item.color}` : ''} - Qty {item.qty}
                              </p>
                            ))}
                            {order.items.length > 2 && (
                              <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">+{order.items.length - 2} more</p>
                            )}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-zinc-400">No orders yet. Your purchased tees will appear here after checkout.</p>
                )}
              </div>
              <div className="rounded-3xl border border-white/10 bg-cynos-card p-6">
                <div className="flex items-center gap-3 text-white">
                  <MapPin className="h-5 w-5 text-cynos-red" />
                  <h2 className="text-lg font-semibold">Saved Addresses</h2>
                </div>
                {customer?.shipping_address?.address_line1 ? (
                  <div className="mt-4 rounded-2xl border border-white/10 bg-black/50 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Default Shipping</p>
                    <p className="mt-3 text-sm leading-6 text-zinc-300">
                      {customer.shipping_address.address_line1}
                      {customer.shipping_address.address_line2 ? `, ${customer.shipping_address.address_line2}` : ''}
                      <br />
                      {customer.shipping_address.city}, {customer.shipping_address.state} {customer.shipping_address.pincode}
                      <br />
                      {customer.shipping_address.country || 'India'}
                    </p>
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-zinc-400">Add a shipping address from Edit Profile. Keep your delivery details ready for faster checkout.</p>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <Link to="/shipping-returns" className="block rounded-3xl border border-white/10 bg-cynos-card p-6 text-white transition hover:border-cynos-red">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Policy</p>
                  <p className="mt-3 text-lg font-semibold">Shipping & Returns</p>
                </div>
                <ArrowRight className="h-5 w-5 text-cynos-red" />
              </div>
            </Link>
            <Link to="/track-order" className="block rounded-3xl border border-white/10 bg-cynos-card p-6 text-white transition hover:border-cynos-red">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Support</p>
                  <p className="mt-3 text-lg font-semibold">Track Order</p>
                </div>
                <ArrowRight className="h-5 w-5 text-cynos-red" />
              </div>
            </Link>
            <Link to="/contact" className="block rounded-3xl border border-white/10 bg-cynos-card p-6 text-white transition hover:border-cynos-red">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-cynos-red">Contact</p>
                  <p className="mt-3 text-lg font-semibold">Contact Support</p>
                </div>
                <ArrowRight className="h-5 w-5 text-cynos-red" />
              </div>
            </Link>
          </motion.div>
        </section>
      </motion.div>
    </main>
  );
}
